import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MessageSquare, Loader2, Send, Trash2, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { waCampaigns as api, ai } from "@/lib/api";
import { formatDate, formatIDR } from "@/lib/format";

const templates = {
  repeat_order: {
    label: "Repeat Order Reminder",
    text: "Halo {nama}! 🌱 Serum Brightening yang kamu beli 30 hari lalu biasanya habis di minggu ini. Klaim cashback Rp15.000 untuk repeat order + free ongkir. Klik: {link}",
  },
  winback: {
    label: "Winback Customer",
    text: "Halo {nama}, kangen banget nih! 💚 Khusus buat kamu, kami kasih voucher Rp25.000 tanpa min. order. Berlaku sampai akhir minggu. Order sekarang: {link}",
  },
  birthday: {
    label: "Birthday Promo",
    text: "Selamat ulang tahun, {nama}! 🎉 Sebagai hadiah, kamu dapat cashback Rp30.000 + free gift untuk pembelian minggu ini. {link}",
  },
  new_product: {
    label: "New Product Launch",
    text: "Halo {nama}! ✨ Produk baru kami sudah launching hari ini. Dapatkan harga spesial early bird + free gift. Cek: {link}",
  },
  cashback_reminder: {
    label: "Cashback Reminder",
    text: "Halo {nama}, jangan sampai lupa! 💰 Voucher {voucher} kamu berlaku sampai 3 hari lagi. Pakai sekarang: {link}",
  },
  referral: {
    label: "Referral Invitation",
    text: "Halo {nama}! 🎁 Ajak teman order & kalian berdua dapat cashback Rp20.000. Kode referral kamu: {kode}. Share ke temenmu: {link}",
  },
};

const emptyForm = { campaign_name: "", campaign_type: "repeat_order", segment: "all", message_template: templates.repeat_order.text, scheduled_at: "" };

export default function WACampaigns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [aiLoading, setAiLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try { setRows(await api.list()); } catch (err) { console.warn("WA campaigns load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const setType = (t) => setForm({ ...form, campaign_type: t, message_template: templates[t]?.text || form.message_template });

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const res = await ai.generate({ prompt_type: "wa_campaign", context: `Tipe campaign: ${templates[form.campaign_type]?.label}. Target: segment ${form.segment}. Buat pesan WhatsApp yang mendorong action.` });
      setForm({ ...form, message_template: res.result });
      toast.success("Pesan digenerate oleh AI");
    } catch { toast.error("AI gagal, coba lagi"); }
    setAiLoading(false);
  };

  const create = async () => {
    if (!form.campaign_name) { toast.error("Nama campaign wajib"); return; }
    try {
      await api.create(form);
      toast.success("Campaign dibuat");
      setOpen(false); setForm(emptyForm); fetchList();
    } catch { toast.error("Gagal"); }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm("Kirim campaign ini? Sistem akan generate link WhatsApp untuk setiap recipient.")) return;
    try {
      const res = await api.send(id);
      toast.success(`Campaign disiapkan untuk ${res.sent_count} pelanggan`);
      setPreview(res);
      fetchList();
    } catch { toast.error("Gagal kirim"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus campaign?")) return;
    await api.remove(id); toast.success("Dihapus"); fetchList();
  };

  const buildWaLink = (customer, msg) => {
    const personalizedMsg = msg
      .replace(/{nama}/gi, customer.name)
      .replace(/{link}/gi, "")
      .replace(/{voucher}/gi, customer.referral_code || "REWARD")
      .replace(/{kode}/gi, customer.referral_code || "REF");
    const num = (customer.whatsapp_number || "").replace(/\D/g, "");
    return `https://wa.me/${num}?text=${encodeURIComponent(personalizedMsg)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">WhatsApp Campaign Center</h1>
          <p className="text-sm text-gray-500 mt-1">Kirim pesan repeat order, winback, dan promo ke pelanggan Anda.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-wa-btn"><Plus className="mr-2 h-4 w-4" /> Buat Campaign</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Buat WhatsApp Campaign</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><Label>Nama Campaign</Label><Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="Contoh: Reorder Serum Nov" data-testid="wa-name" /></div>
                <div>
                  <Label>Tipe</Label>
                  <Select value={form.campaign_type} onValueChange={setType}>
                    <SelectTrigger data-testid="wa-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(templates).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Segment</Label>
                  <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                    <SelectTrigger data-testid="wa-segment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Customer</SelectItem>
                      <SelectItem value="new">Baru</SelectItem>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="repeat">Repeat Buyer</SelectItem>
                      <SelectItem value="loyal">Loyal</SelectItem>
                      <SelectItem value="at_risk">Berisiko</SelectItem>
                      <SelectItem value="inactive">Non-aktif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pesan (variabel: {"{nama}"}, {"{link}"}, {"{voucher}"})</Label>
                  <Textarea rows={7} value={form.message_template} onChange={(e) => setForm({ ...form, message_template: e.target.value })} data-testid="wa-message" />
                  <Button size="sm" variant="outline" className="mt-2" onClick={generateAI} disabled={aiLoading} data-testid="ai-generate-wa">
                    {aiLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : "✨"} Generate dengan AI
                  </Button>
                </div>
              </div>

              <div>
                <Label>Preview WhatsApp</Label>
                <div className="mt-1.5 bg-[#e5ddd5] rounded-lg p-4 min-h-[300px]">
                  <div className="bg-[#dcf8c6] rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                    <div className="text-xs text-gray-700 whitespace-pre-wrap">{form.message_template.replace(/{nama}/gi, "Sarah").replace(/{link}/gi, "own.co/order").replace(/{voucher}/gi, "OWN12345").replace(/{kode}/gi, "REF001")}</div>
                    <div className="text-[10px] text-gray-500 text-right mt-1">10:23 ✓✓</div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-wa-btn">Simpan Campaign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada campaign</h3>
              <p className="mt-1 text-sm text-gray-500">Buat campaign WhatsApp pertama Anda untuk repeat order.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {rows.map((c) => (
                <div key={c.id} className="p-4 hover:bg-gray-50" data-testid={`wa-campaign-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-gray-900">{c.campaign_name}</div>
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px] capitalize">{c.campaign_type.replace(/_/g, " ")}</Badge>
                        <Badge className={`border text-[10px] ${c.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{c.status}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">{c.message_template}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                        <span>Segment: <b className="text-gray-700 capitalize">{c.segment}</b></span>
                        <span>Sent: <b className="text-gray-700">{c.sent_count || 0}</b></span>
                        <span>Revenue: <b className="text-emerald-600">{formatIDR(c.revenue_generated || 0)}</b></span>
                        <span>Dibuat: {formatDate(c.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => sendCampaign(c.id)} data-testid={`send-wa-${c.id}`}><Send size={14} className="mr-1" /> Kirim</Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(c.id)}><Trash2 size={14} className="text-red-500" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Card>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Kirim ke Pelanggan ({preview?.sent_count || 0})</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Klik nomor untuk buka WhatsApp dengan pesan yang sudah dipersonalisasi:</p>
          <div className="max-h-96 overflow-y-auto space-y-1 border rounded-lg p-2">
            {preview?.recipients?.slice(0, 50).map((r) => (
              <a key={r.id} href={buildWaLink(r, rows.find(c => c.status === "sent")?.message_template || "")} target="_blank" rel="noreferrer"
                className="flex items-center justify-between p-2 rounded hover:bg-emerald-50 text-sm" data-testid={`send-to-${r.id}`}>
                <div>
                  <div className="font-semibold text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">{r.whatsapp_number}</div>
                </div>
                <Eye size={16} className="text-emerald-600" />
              </a>
            ))}
          </div>
          <p className="text-xs text-gray-500">Menampilkan 50 pertama. Untuk skala besar, upgrade ke Growth plan untuk WhatsApp Business API otomatis.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
